import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, PageHeader, Skeleton } from '../../shared/design';
import { getBackendService } from '../../shared/services/apiConfig';
import { computeAnalytics } from '../../shared/analytics/compute';
import type { AnalyticsModel, AnalyticsView, ViewKey } from '../../shared/analytics/types';
import { DonutChart, GroupedBarsChart, HBarsChart, HeatmapChart, LineAreaChart, PALETTE, Sparkline } from './analytics/AnalyticsCharts';
import DetailedReport from './DetailedReport';

/**
 * Analyses — même lecture que la fiche « Analyses » du site : un panneau
 * bordeaux, 4 indicateurs clés, 6 vues. Tout est calculé à partir des
 * données réelles du restaurant (visites validées, caisse, membres,
 * récompenses, segments) par `computeAnalytics`.
 */

const TABS: Array<{ key: ViewKey; label: string }> = [
    { key: 'revenue', label: "Chiffre d'affaires" },
    { key: 'retention', label: 'Rétention' },
    { key: 'points', label: 'Points' },
    { key: 'traffic', label: 'Affluence' },
    { key: 'customers', label: 'Clientèle' },
    { key: 'frequency', label: 'Fréquence' },
];

const ChartFor: React.FC<{ view: AnalyticsView }> = ({ view }) => {
    switch (view.kind) {
        case 'line': return <LineAreaChart view={view} />;
        case 'groupedBars': return <GroupedBarsChart view={view} />;
        case 'heatmap': return <HeatmapChart view={view} />;
        case 'donut': return <DonutChart view={view} />;
        case 'hbars': return <HBarsChart view={view} />;
    }
};

/** Données du graphique pour les lecteurs d'écran (le SVG est masqué pour eux). Le titre du
 *  tableau est la légende visible sous le graphique (aria-labelledby), sans la répéter. */
const ViewTable: React.FC<{ view: AnalyticsView; labelledBy: string }> = ({ view, labelledBy }) => {
    const fmt = (v: number | null) => (v === null ? 'non mesuré' : v.toLocaleString('fr-FR', { maximumFractionDigits: 1 }));
    let head: string[] = [];
    let rows: string[][] = [];
    switch (view.kind) {
        case 'line':
            head = ['Mois', view.unit === 'euro' ? 'Montant (€)' : 'Taux (%)'];
            rows = view.points.map((p) => [p.label, fmt(p.value)]);
            break;
        case 'groupedBars':
            head = ['Mois', view.a.name, view.b.name];
            rows = view.labels.map((l, i) => [l, fmt(view.a.values[i]), fmt(view.b.values[i])]);
            break;
        case 'heatmap':
            head = ['Service', ...view.days];
            rows = view.rows.map((r) => [r.name, ...r.values.map(fmt)]);
            break;
        case 'donut':
            head = ['Segment', 'Membres'];
            rows = view.segments.map((s) => [s.name, fmt(s.value)]);
            break;
        case 'hbars':
            head = ['Visites sur 30 jours', 'Part des membres (%)'];
            rows = view.rows.map((r) => [r.name, fmt(r.value)]);
            break;
    }
    return (
        <table className="sr-only" aria-labelledby={labelledBy}>
            <thead><tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => (j === 0 ? <th key={j} scope="row">{c}</th> : <td key={j}>{c}</td>))}</tr>)}</tbody>
        </table>
    );
};

const Tile: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
    <div className={`rounded-2xl p-4 sm:p-5 ${className}`} style={{ background: 'rgba(255,245,243,0.06)' }}>{children}</div>
);

const LoyaltyAnalytics: React.FC = () => {
    const [model, setModel] = useState<AnalyticsModel | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<ViewKey>('revenue');
    const [report, setReport] = useState<null | 'standard' | 'custom'>(null);
    const aliveRef = useRef(true);
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const load = useCallback(async () => {
        setError(null);
        setModel(null);
        try {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            if (!user?.restaurantId) { setError('Aucun établissement associé à ce compte.'); return; }
            const raw = await (backend as any).getAnalyticsRaw(user.restaurantId);
            if (aliveRef.current) setModel(computeAnalytics(raw));
        } catch (e: any) {
            console.error('Analyses :', e);
            if (aliveRef.current) setError("Impossible de charger les analyses pour l'instant. Vérifiez la connexion puis réessayez.");
        }
    }, []);

    useEffect(() => {
        aliveRef.current = true;
        load();
        return () => { aliveRef.current = false; };
    }, [load]);

    const view = useMemo(() => model?.views[tab], [model, tab]);
    const computedAtLabel = model
        ? model.computedAt.toLocaleString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
        : '';

    // Onglets : motif ARIA complet (flèches, Début/Fin, un seul onglet tabulable).
    const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        const i = TABS.findIndex((t) => t.key === tab);
        const go = (j: number) => {
            const next = TABS[(j + TABS.length) % TABS.length].key;
            setTab(next);
            tabRefs.current[next]?.focus();
        };
        if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
        else if (e.key === 'Home') { e.preventDefault(); go(0); }
        else if (e.key === 'End') { e.preventDefault(); go(TABS.length - 1); }
    };

    return (
        <div className="space-y-6 sm:space-y-8">
            <PageHeader
                title="Analyses"
                subtitle="Ce que votre programme rapporte, calculé sur vos visites validées. Survolez les graphiques pour le détail."
                actions={
                    <Button variant="outline" size="sm" onClick={() => setReport('custom')}>Rapport détaillé</Button>
                }
            />

            {error && (
                <div role="alert" className="rounded-2xl border border-brand-200 bg-brand-50 px-5 py-4 text-sm text-brand-800">
                    <p>{error}</p>
                    <Button variant="primary" size="sm" className="mt-3" onClick={load}>Réessayer</Button>
                </div>
            )}

            {error ? null : model?.empty ? (
                /* Aucune visite validée : on dit ce qui va apparaître, sans graphique à zéro. */
                <section className="rounded-[28px] border border-black/[0.06] bg-white p-6 shadow-sm sm:p-8">
                    <div className="max-w-2xl">
                        <p className="section-title text-xl">Pas encore de visite validée</p>
                        <p className="mt-2 text-[15px] leading-relaxed text-gray-600">
                            Les analyses se construisent à partir des visites validées par vos clients (tap NFC, QR, clôture de table).
                            Dès la première, cet écran se remplit tout seul — rien n'est estimé ni simulé.
                        </p>
                    </div>
                    <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {[
                            ['Chiffre d\'affaires', 'CA réalisé avec vos membres, mois par mois.'],
                            ['Rétention', 'Part des clients qui reviennent sous 30 jours.'],
                            ['Points', 'Points gagnés et récompenses utilisées.'],
                            ['Affluence', 'Visites par service, midi et soir, sur 4 semaines.'],
                            ['Clientèle', 'Répartition par segment (nouveaux, habitués, VIP…).'],
                            ['Fréquence', 'Nombre de visites par membre sur 30 jours.'],
                        ].map(([t, d]) => (
                            <li key={t} className="rounded-2xl bg-cream px-4 py-3">
                                <p className="text-sm font-semibold text-gray-900">{t}</p>
                                <p className="mt-0.5 text-sm text-gray-600">{d}</p>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-6 text-sm text-gray-600">
                        {model.kpis[3] && <>Membres inscrits à ce jour : <b className="text-gray-900">{model.memberCount}</b>. </>}
                        Pour démarrer : préparez une addition dans <b>Encaissement fidélité</b> et faites taper la puce au client.
                    </p>
                </section>
            ) : (
            <section className="rounded-[28px] p-4 sm:p-6 lg:p-8 text-cream shadow-lg" style={{ background: PALETTE.ink }} aria-busy={!model}>
                {/* Indicateurs clés */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {(model?.kpis ?? Array.from({ length: 4 })).map((k: any, i) => (
                        <Tile key={i}>
                            {k ? (
                                <>
                                    <p className="text-[13px]" style={{ color: PALETTE.axis }}>{k.label}</p>
                                    <p className="mt-1 font-stat text-[2rem] leading-none" style={{ color: '#f6c9d1' }}>{k.value}</p>
                                    <div className="mt-3 flex items-end justify-between gap-3">
                                        <p className="text-xs" style={{ color: k.delta === null ? PALETTE.axis : k.deltaPositive ? '#7fd39a' : '#f08a8a' }}>
                                            {k.delta === null ? 'Pas encore de comparaison' : <><span aria-hidden="true">{k.deltaPositive ? '▲' : '▼'}</span> {k.delta} vs {model!.comparisonLabel}</>}
                                        </p>
                                        <Sparkline values={k.spark} />
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-3">
                                    <Skeleton variant="text" width="50%" className="!bg-white/10" />
                                    <Skeleton variant="text" width="40%" height={30} className="!bg-white/10" />
                                    <Skeleton variant="text" width="70%" className="!bg-white/10" />
                                </div>
                            )}
                        </Tile>
                    ))}
                </div>

                {/* Onglets */}
                <div className="mt-6 inline-flex max-w-full overflow-x-auto scroll-thin rounded-full p-1" style={{ background: 'rgba(255,245,243,0.08)' }} role="tablist" aria-label="Vues des analyses">
                    {TABS.map((t) => {
                        const active = t.key === tab;
                        return (
                            <button
                                key={t.key}
                                ref={(el) => { tabRefs.current[t.key] = el; }}
                                id={`analytics-tab-${t.key}`}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                aria-controls="analytics-panel"
                                tabIndex={active ? 0 : -1}
                                onKeyDown={onTabKeyDown}
                                onClick={() => setTab(t.key)}
                                className={`h-9 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors ${active ? 'bg-cream text-gray-900 shadow-sm' : 'hover:text-cream'}`}
                                style={active ? undefined : { color: PALETTE.axis }}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* Corps : graphique + statistiques */}
                <div id="analytics-panel" role="tabpanel" aria-labelledby={`analytics-tab-${tab}`} className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="min-w-0 lg:col-span-2">
                        {view ? (
                            <div key={tab} className="animate-fade-in">
                                <div aria-hidden="true"><ChartFor view={view} /></div>
                                <ViewTable view={view} labelledBy="analytics-caption" />
                            </div>
                        ) : (
                            <Skeleton variant="rectangular" width="100%" height={300} className="!bg-white/10" />
                        )}
                        <p id="analytics-caption" className="mt-4 text-sm" style={{ color: PALETTE.text }}>{view?.caption}</p>
                        {model && (
                            <p className="mt-1 text-xs" style={{ color: PALETTE.axis }}>
                                Calculé le {computedAtLabel} sur les 6 derniers mois (rechargez l'écran pour mettre à jour).
                            </p>
                        )}
                    </div>
                    <div className="space-y-3">
                        {(view?.stats ?? Array.from({ length: 3 })).map((s: any, i) => (
                            <Tile key={i}>
                                {s ? (
                                    <>
                                        <p className="text-[13px]" style={{ color: PALETTE.axis }}>{s.label}</p>
                                        <p className="mt-1 font-stat text-2xl leading-tight">{s.value}</p>
                                        {s.hint && <p className="mt-0.5 text-xs" style={{ color: PALETTE.axis }}>{s.hint}</p>}
                                    </>
                                ) : (
                                    <div className="space-y-2">
                                        <Skeleton variant="text" width="60%" className="!bg-white/10" />
                                        <Skeleton variant="text" width="40%" height={24} className="!bg-white/10" />
                                    </div>
                                )}
                            </Tile>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {report && <DetailedReport type={report} onClose={() => setReport(null)} />}
        </div>
    );
};

export default LoyaltyAnalytics;
